import { cloneElement, type ReactElement, type ReactNode, useEffect, useRef } from 'react';

import { cn } from '@shared/lib/utils';
import { Tabs, TabsList, TabsTrigger } from './tabs';
import { tabsListVariants, tabsTriggerVariants } from './tabs-variants';

export interface SectionTabItem {
  id: string;
  label: ReactNode;
  disabled?: boolean;
  availabilityLabel?: string;
}

interface SectionTabsProps {
  variant?: 'primary' | 'secondary';
  items: SectionTabItem[];
  activeId?: string;
  onSelect?: (id: string) => void;
  onActiveSelect?: (id: string) => void;
  renderLink?: (
    item: SectionTabItem,
    options: { className: string; isActive: boolean }
  ) => ReactElement;
  className?: string;
  /** Use wrapped navigation when the section must never require horizontal scrolling on mobile. */
  mobileWrap?: boolean;
}

export function SectionTabs({
  items,
  activeId,
  onSelect,
  onActiveSelect,
  renderLink,
  className,
  variant = 'primary',
  mobileWrap = false,
}: SectionTabsProps) {
  const fallbackId = items[0]?.id ?? '';
  const resolvedActiveId = activeId ?? fallbackId;
  const listRef = useRef<HTMLDivElement>(null);
  const triggerClassName =
    variant === 'primary'
      ? 'h-auto min-h-11 shrink-0 whitespace-normal py-2 text-left font-sans text-base font-medium leading-6 tracking-[0.025em] sm:text-center'
      : undefined;

  useEffect(() => {
    if (mobileWrap) return;
    const list = listRef.current;
    const activeItem = list?.querySelector<HTMLElement>(
      '[aria-current="page"], [data-state="active"]'
    );
    if (
      list === null ||
      activeItem === undefined ||
      activeItem === null ||
      list.scrollWidth <= list.clientWidth
    ) {
      return;
    }

    const centeredLeft =
      activeItem.offsetLeft - Math.max(0, (list.clientWidth - activeItem.offsetWidth) / 2);
    list.scrollTo({ left: Math.max(0, centeredLeft), behavior: 'auto' });
  }, [mobileWrap, resolvedActiveId]);
  const listClassName = mobileWrap
    ? 'w-full min-w-0 flex-wrap overflow-visible'
    : 'w-full min-w-0 flex-nowrap overflow-x-auto sm:flex-wrap sm:overflow-visible';

  if (renderLink) {
    return (
      <div className={cn('w-full min-w-0', className)}>
        <div
          ref={listRef}
          data-mm-variant={variant}
          className={cn(tabsListVariants({ variant }), listClassName)}
        >
          {items.map((item) => {
            const isActive = item.id === resolvedActiveId;
            const itemClassName = cn(tabsTriggerVariants({ variant }), triggerClassName);
            const content = (
              <>
                <span>{item.label}</span>
                {item.disabled && item.availabilityLabel ? (
                  <span className="sr-only"> — {item.availabilityLabel}</span>
                ) : null}
              </>
            );

            if (!item.disabled) {
              const link = renderLink(
                { ...item, label: content },
                { className: itemClassName, isActive }
              );
              // SectionTabs owns selection for both router links and button triggers.
              return cloneElement(
                link as ReactElement<{
                  'aria-current'?: 'page';
                  'data-state'?: 'active' | 'inactive';
                }>,
                {
                  key: item.id,
                  'aria-current': isActive ? 'page' : undefined,
                  'data-state': isActive ? 'active' : 'inactive',
                }
              );
            }

            return (
              <button
                key={item.id}
                type="button"
                className={itemClassName}
                data-state={isActive ? 'active' : 'inactive'}
                disabled
              >
                {content}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <Tabs
      value={resolvedActiveId}
      onValueChange={(value) => {
        onSelect?.(value);
      }}
      className={cn('w-full min-w-0', className)}
    >
      <TabsList ref={listRef} variant={variant} className={listClassName}>
        {items.map((item) => {
          const content: ReactNode = (
            <>
              <span>{item.label}</span>
              {item.disabled && item.availabilityLabel ? (
                <span className="sr-only"> — {item.availabilityLabel}</span>
              ) : null}
            </>
          );
          const triggerProps = {
            variant,
            value: item.id,
            className: triggerClassName,
            disabled: item.disabled,
            'aria-current': item.id === resolvedActiveId ? ('page' as const) : undefined,
            onClick: () => {
              if (item.id === resolvedActiveId) {
                onActiveSelect?.(item.id);
              }
            },
          };

          return (
            <TabsTrigger key={item.id} {...triggerProps}>
              {content}
            </TabsTrigger>
          );
        })}
      </TabsList>
    </Tabs>
  );
}
